// src/components/boletos.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { collection, query, onSnapshot, writeBatch, doc, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
// MODIFICACIÓN: Se cambian los íconos importados.
import { Ticket, RefreshCw } from 'lucide-react';

// --- Configuración de Firebase (SIN CAMBIOS) ---
const appId = typeof __app_id !== 'undefined' ? __app_id : 'default-raffle-app';
const firebaseConfig = {
  apiKey: "AIzaSyD3zTS6ijHH0_HsaNPtvDm-eIlyUw3x18o",
  authDomain: "rifas-el-pri.firebaseapp.com",
  projectId: "rifas-el-pri",
  storageBucket: "rifas-el-pri.firebasestorage.app",
  messagingSenderId: "1070599909067",
  appId: "1:1070599909067:web:6da522093d4a60c040b54e",
  measurementId: "G-1KC9D6VZY3"
};


// --- Componentes de UI ---

// El componente de botón de boleto ahora acepta `isJiggling` para animación.
const TicketButton = React.memo(({ ticket, isSelected, isJiggling, onClick }) => {
    let stateClasses = 'bg-gray-100 text-gray-800 hover:bg-gray-200'; // Disponible
    if (ticket.status === 'reserved') {
        // Estilo visual fuerte para boletos ya vendidos / bloqueados
        stateClasses = 'bg-gray-800 text-gray-500 cursor-not-allowed opacity-100 border border-gray-600'; // Agotado / Bloqueado
    } else if (isSelected) {
        stateClasses = 'bg-green-500 text-white ring-2 ring-green-700 transform scale-110 shadow-lg z-10'; // Seleccionado
    }
    return (
        <button
            onClick={() => onClick(ticket.id)}
            disabled={ticket.status === 'reserved'}
            className={`w-full h-9 flex items-center justify-center rounded-lg font-mono transition-all duration-200 text-sm shadow-sm ${stateClasses} ${isJiggling ? 'animate-shake' : ''}`}
        >
            {ticket.number}
        </button>
    );
});

// NUEVO: Componente para la vista de confirmación después de apartar boletos.
const ConfirmationView = ({ onBack }) => (
    <div className="flex flex-col items-center justify-center h-full text-center p-6 bg-white rounded-lg">
        <h2 className="text-2xl font-bold text-green-700 mb-4">¡Felicidades, tus boletos están apartados!</h2>
        <p className="text-gray-800 mb-6 max-w-md">
           Para completar tu compra, transfiere o deposita a la siguiente cuenta y envía tu comprobante por WhatsApp.
        </p>
        <div className="bg-gray-100 p-4 rounded-lg border border-gray-200 w-full max-w-sm mb-6">
            <p className="font-semibold text-lg">BBVA</p>
            <p className="text-gray-600">No. de Cuenta: <span className="font-mono">1234 5678 9012 3456</span></p>
            <p className="text-gray-600">CLABE: <span className="font-mono">123180001234567890</span></p>
        </div>
        <div className="flex items-center justify-center space-x-8 mb-8">
            <img src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSIxlTMO0sxbNkgOqVbDmQqu3f7ASFaGUXcvw&s" alt="Logo BBVA" className="h-10 sm:h-12"/>
            <div className="text-left">
                <p className="font-semibold">Enviar comprobante a:</p>
                 <a href="https://wa.me/524771811622" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-green-600 hover:text-green-800 transition-colors">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/6/6b/WhatsApp.svg/2044px-WhatsApp.svg.png" alt="Logo WhatsApp" className="h-6 sm:h-7"/>
                    <span className="font-bold text-lg">477 181 1622</span>
                </a>
            </div>
        </div>
        <button onClick={onBack} className="bg-green-600 text-white font-bold py-3 px-8 rounded-full text-base hover:bg-green-700 transition-all transform hover:scale-105">
           Volver al inicio
        </button>
    </div>
);


// --- Componente Principal de Boletos (Modificado) ---

const Boletos = ({ onBack, onGoToRegister, showConfirmationPage = false }) => {
    const [allTickets, setAllTickets] = useState([]);
    // MODIFICACIÓN: Se maneja un array de IDs para los boletos a mostrar.
    const [displayTicketIds, setDisplayTicketIds] = useState([]);
    const [selectedTickets, setSelectedTickets] = useState(new Set());
    
    // Aumentado a 99,000 boletos como solicitó el usuario.
    // Nota: la cuadrícula muestra una muestra aleatoria de 100 boletos para evitar renderizar 99k elementos en el DOM.
    const TOTAL_TICKETS = 99000;
    // Extend expiration window to 24 hours to avoid prematurely deleting reservations
    const TICKET_EXPIRATION_HOURS = 24;
    const initialTicketsGenerated = useRef(false);

    // Efecto para escuchar los cambios en Firebase y actualizar el estado de los boletos.
    useEffect(() => {
        const baseTickets = Array.from({ length: TOTAL_TICKETS }, (_, i) => ({
            id: i,
            number: i.toString().padStart(5, '0'),
            status: 'available',
        }));
        
        const reservationsCol = collection(db, `artifacts/${appId}/public/data/reservations`);
        const q = query(reservationsCol);
        
        const unsubscribe = onSnapshot(q, async (snapshot) => {
            const reservedTicketsSet = new Set();
            const now = Date.now();
            const batch = writeBatch(db);
            let hasExpiredReservations = false;

            for (const docSnapshot of snapshot.docs) {
                const reservation = docSnapshot.data();
                if (reservation.createdAt) {
                    const reservationTime = reservation.createdAt.toDate().getTime();
                    const isExpired = now - reservationTime > TICKET_EXPIRATION_HOURS * 60 * 60 * 1000;
                    const isConfirmed = !!reservation.confirmed; // true when user completed registration
                    const status = (reservation.status || '').toString().toLowerCase();
                    const isLiquidado = status === 'liquidado';

                    // Only delete if expired AND NOT confirmed AND NOT liquidado (paid)
                    if (isExpired && !isConfirmed && !isLiquidado) {
                        batch.delete(doc(db, `artifacts/${appId}/public/data/reservations`, docSnapshot.id));
                        hasExpiredReservations = true;
                    } else {
                        // Keep reserved tickets in the set so they render as reserved in the UI
                        if (Array.isArray(reservation.tickets)) {
                            reservation.tickets.forEach(ticketNumber => {
                                const ticketId = parseInt(ticketNumber, 10);
                                if(!isNaN(ticketId)) reservedTicketsSet.add(ticketId);
                            });
                        }
                    }
                }
            }
            
            if (hasExpiredReservations) await batch.commit();

            setAllTickets(baseTickets.map(ticket => ({
                ...ticket,
                status: reservedTicketsSet.has(ticket.id) ? 'reserved' : 'available'
            })));
        });

        return () => unsubscribe();
    }, []);

    // NUEVO: Función para generar 100 boletos aleatorios para mostrar.
    const generateRandomTickets = useCallback(() => {
        // Haptic feedback
        vibrate(30);
        // Mezclar todo el conjunto de boletos (incluye disponibles y reservados)
        if (!allTickets || allTickets.length === 0) { setDisplayTicketIds([]); return; }
        const shuffled = [...allTickets].sort(() => 0.5 - Math.random());
        // Mostrar hasta 100 boletos aleatorios para dar sensación de demanda
        const count = Math.min(100, shuffled.length);
        const randomIds = shuffled.slice(0, count).map(t => t.id);
        setDisplayTicketIds(randomIds);
    }, [allTickets]);

    // Efecto para generar los boletos iniciales una sola vez cuando los datos están listos.
    useEffect(() => {
        if (allTickets.length > 0 && !initialTicketsGenerated.current) {
            if (allTickets.some(t => t.status === 'available')) {
                generateRandomTickets();
                initialTicketsGenerated.current = true;
            }
        }
    }, [allTickets, generateRandomTickets]);

    // MODIFICACIÓN: `ticketsToShow` ahora se calcula basado en `displayTicketIds`.
    const ticketsToShow = useMemo(() => {
        const ticketsMap = new Map(allTickets.map(t => [t.id, t]));
        return displayTicketIds.map(id => ticketsMap.get(id)).filter(Boolean);
    }, [allTickets, displayTicketIds]);
    // State for which tickets are currently jiggling (oscillating)
    const [jigglingIds, setJigglingIds] = useState(new Set());
    
    const handleTicketClick = useCallback((ticketId) => {
        // Haptic feedback on ticket selection
        vibrate(20);
        const ticket = allTickets.find(t => t.id === ticketId);
        if (!ticket || ticket.status === 'reserved') return;

        setSelectedTickets(prev => {
            const newSelection = new Set(prev);
            if (newSelection.has(ticketId)) newSelection.delete(ticketId);
            else newSelection.add(ticketId);
            return newSelection;
        });
    }, [allTickets]);

    // NUEVO: Función para seleccionar un paquete de boletos al azar.
    const handleSelectPackage = useCallback((count) => {
        // Haptic feedback on package selection
        vibrate(25);
        const available = allTickets.filter(t => t.status === 'available');
        if (available.length < count) {
            console.warn(`No hay suficientes boletos disponibles para seleccionar ${count}.`);
            // Aquí se podría mostrar una alerta al usuario.
            return;
        }
        const shuffled = available.sort(() => 0.5 - Math.random());
        const newSelection = new Set(shuffled.slice(0, count).map(t => t.id));
        setSelectedTickets(newSelection);
    }, [allTickets]);

    // Effect: periodically pick up to 10 visible tickets and apply a left-right shake for 1s, then wait 3s and repeat
    useEffect(() => {
        if (!ticketsToShow || ticketsToShow.length === 0) return;
        let active = true;
        let timer = null;

        const runCycle = () => {
            if (!active) return;
            const count = Math.min(10, ticketsToShow.length);
            const ids = ticketsToShow.map(t => t.id);
            const chosen = new Set();
            while (chosen.size < count) {
                const rand = ids[Math.floor(Math.random() * ids.length)];
                chosen.add(rand);
            }
            setJigglingIds(chosen);
            // stop shake after 1s
            timer = setTimeout(() => {
                setJigglingIds(new Set());
                // wait 3s then run again
                timer = setTimeout(() => runCycle(), 3000);
            }, 1000);
        };

        // initial small delay
        timer = setTimeout(() => runCycle(), 700);

        return () => {
            active = false;
            if (timer) clearTimeout(timer);
        };
    }, [ticketsToShow]);

    const selectedTicketNumbers = useMemo(() => 
        Array.from(selectedTickets)
             .map(id => allTickets.find(t => t.id === id)?.number)
             .filter(Boolean)
             .sort(), 
    [selectedTickets, allTickets]);

    // Haptic feedback helper
    const vibrate = useCallback((ms = 50) => {
        if (window && window.navigator && window.navigator.vibrate) {
            try { window.navigator.vibrate(ms); } catch (e) { /* ignore */ }
        }
    }, []);

    // Lógica para apartar boletos (SIN CAMBIOS).
    const handleApartarClick = async () => {
        if (selectedTickets.size === 0) return;

        // Crear una reservación provisional en Firestore para bloquear los boletos inmediatamente.
        try {
            const reservationsCol = collection(db, `artifacts/${appId}/public/data/reservations`);
            console.log('Creando reservación provisional en:', reservationsCol.path, 'tickets:', selectedTicketNumbers);
            const docRef = await addDoc(reservationsCol, {
                tickets: selectedTicketNumbers,
                createdAt: serverTimestamp(),
                status: 'pending'
            });
            console.log('Reservación creada con id:', docRef.id);

            // Pasamos el id de la reservación al flujo de registro para que al confirmar
            // se pueda actualizar la misma reservación en lugar de crear otra.
            onGoToRegister({
                selectedTicketNumbers: selectedTicketNumbers,
                reservationId: docRef.id
            });
        } catch (err) {
            console.error('Error creando la reservación provisional:', err);
            // Si falla la escritura, igual navegamos al registro (opcional)
            onGoToRegister({ selectedTicketNumbers: selectedTicketNumbers });
        }
    };
    
    // NUEVO: Si la prop `showConfirmationPage` es verdadera, muestra la vista de confirmación.
    if (showConfirmationPage) {
        return (
             <div className="fixed inset-0 bg-gray-50 z-50 flex flex-col font-sans">
                <ConfirmationView onBack={onBack} />
             </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-gray-50 z-50 flex flex-col font-sans overflow-hidden">
            {/* Oscillate keyframes for ticket attraction */}
            <style>{`
                @keyframes oscillate {
                  0% { transform: translateY(0) rotate(0deg); }
                  40% { transform: translateY(-6px) rotate(-3deg); }
                  60% { transform: translateY(4px) rotate(3deg); }
                  100% { transform: translateY(0) rotate(0deg); }
                }
                .animate-oscillate { animation: oscillate 0.35s ease-in-out infinite; }
                                @keyframes attention-bounce {
                                    0% { transform: scale(1); }
                                    40% { transform: scale(1.08); }
                                    70% { transform: scale(0.98); }
                                    100% { transform: scale(1); }
                                }
                                .animate-attention { animation: attention-bounce 800ms ease-in-out infinite; transform-origin: center; }
                                @keyframes shake-horizontal {
                                    0% { transform: translateX(0); }
                                    25% { transform: translateX(-6px); }
                                    50% { transform: translateX(6px); }
                                    75% { transform: translateX(-4px); }
                                    100% { transform: translateX(0); }
                                }
                                .animate-shake { animation: shake-horizontal 1s ease-in-out; }
            `}</style>
            <header className="flex-shrink-0 bg-white shadow-md z-20 p-3 flex items-center justify-center relative">
                <button onClick={onBack} className="absolute top-1/2 -translate-y-1/2 left-3 text-white bg-green-600 rounded-full p-2 hover:bg-green-700 transition-transform transform hover:scale-110">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
                <h2 className="text-2xl font-bold text-gray-800">Comprar Boletos</h2>
            </header>
            
            <main className="flex-grow overflow-y-auto p-3 pb-24">
                 {/* NUEVO: Encabezado y botones de selección de paquetes. */}
                <div className="text-center p-4 bg-white rounded-lg shadow-sm mb-4">
                    <h3 className="text-lg font-semibold text-gray-700 mb-3">
                        Selecciona manualmente los que gustes o elige un paquete:
                    </h3>
                    <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
                        {/* Precio actualizado: $20 por boleto */}
                        <button onClick={() => handleSelectPackage(1)} className="px-5 py-2 bg-gray-200 text-gray-800 font-semibold rounded-full text-sm hover:bg-gray-300 transition-colors">1 x $20</button>
                        <button onClick={() => handleSelectPackage(3)} className="px-5 py-2 bg-gray-200 text-gray-800 font-semibold rounded-full text-sm hover:bg-gray-300 transition-colors">3 x $60</button>
                        <button onClick={() => handleSelectPackage(5)} className="px-5 py-2 bg-gray-200 text-gray-800 font-semibold rounded-full text-sm hover:bg-gray-300 transition-colors">5 x $100</button>
                        <button onClick={() => handleSelectPackage(10)} className="px-5 py-2 bg-gray-200 text-gray-800 font-semibold rounded-full text-sm hover:bg-gray-300 transition-colors">10 x $200</button>
                        <button onClick={() => handleSelectPackage(30)} className="px-5 py-2 bg-gray-200 text-gray-800 font-semibold rounded-full text-sm hover:bg-gray-300 transition-colors">30 x $600</button>
                         <button onClick={() => handleSelectPackage(50)} className="px-5 py-2 bg-gray-200 text-gray-800 font-semibold rounded-full text-sm hover:bg-gray-300 transition-colors">50 x $1000</button>
                         <button onClick={() => handleSelectPackage(100)} className="px-5 py-2 bg-gray-200 text-gray-800 font-semibold rounded-full text-sm hover:bg-gray-300 transition-colors">100 x $2000</button>
                         <button onClick={() => handleSelectPackage(300)} className="px-5 py-2 bg-gray-200 text-gray-800 font-semibold rounded-full text-sm hover:bg-gray-300 transition-colors">300 x $6000</button>
                        <button onClick={() => handleSelectPackage(400)} className="px-5 py-2 bg-gray-200 text-gray-800 font-semibold rounded-full text-sm hover:bg-gray-300 transition-colors">400 x $8000</button>
                        <button onClick={() => handleSelectPackage(500)} className="px-5 py-2 bg-gray-200 text-gray-800 font-semibold rounded-full text-sm hover:bg-gray-300 transition-colors">500 x $10000</button>
                        <button onClick={() => handleSelectPackage(700)} className="px-5 py-2 bg-gray-200 text-gray-800 font-semibold rounded-full text-sm hover:bg-gray-300 transition-colors">700 x $14000</button>
                         <button onClick={() => handleSelectPackage(1000)} className="px-5 py-2 bg-gray-200 text-gray-800 font-semibold rounded-full text-sm hover:bg-gray-300 transition-colors">1000 x $20000</button>
                           <button onClick={() => handleSelectPackage(2000)} className="px-5 py-2 bg-gray-200 text-gray-800 font-semibold rounded-full text-sm hover:bg-gray-300 transition-colors">2000 x $40000</button>
                           <button onClick={() => handleSelectPackage(5000)} className="px-5 py-2 bg-gray-200 text-gray-800 font-semibold rounded-full text-sm hover:bg-gray-300 transition-colors">5000 x $100000</button>
                           <button onClick={() => handleSelectPackage(10000)} className="px-5 py-2 bg-gray-200 text-gray-800 font-semibold rounded-full text-sm hover:bg-gray-300 transition-colors">10000 x $200000</button>
                 









                    </div>
                </div>

                {/* Ruleta removida: no requerida */}

                 {/* NUEVO: Botón para generar nuevos boletos aleatorios. */}
                <div className="text-center mb-4">
                     <button 
                        onClick={generateRandomTickets} 
                        className="bg-gray-700 text-white font-bold py-2 px-5 rounded-full text-base hover:bg-gray-800 transition-all transform hover:scale-105 flex items-center gap-2 mx-auto"
                     >
                        <RefreshCw size={18} />
                        Generar nuevos boletos
                    </button>
                </div>

                {/* MODIFICACIÓN: La cuadrícula de boletos ahora muestra los 100 boletos aleatorios. */}
                <div className="relative min-h-[300px]">
                    {ticketsToShow.length === 0 && (
                        <div className="absolute inset-0 flex items-center justify-center text-gray-500">
                            Cargando boletos...
                        </div>
                    )}
                    {/* Cuadrícula reducida a 5 columnas; mostramos 50 boletos (5 x 10) para mejor rendimiento */}
                    <div className="grid grid-cols-5 gap-2 p-1">
                        {ticketsToShow.map(ticket => (
                            <TicketButton 
                                key={ticket.id} 
                                ticket={ticket} 
                                isSelected={selectedTickets.has(ticket.id)} 
                                onClick={handleTicketClick} 
                            />
                        ))}
                    </div>
                </div>
            </main>

            <footer className="absolute bottom-0 left-0 right-0 bg-white/80 backdrop-blur-sm border-t border-gray-200 p-3 z-20">
                <div className="container mx-auto flex justify-between items-center">
                    <div className="text-black">
                        <span className="font-bold text-lg">{selectedTickets.size}</span>
                        <span className="text-sm text-gray-600"> Seleccionados</span>
                    </div>
                    {(() => {
                        const isActive = selectedTickets.size > 0;
                        return (
                            <button
                                onClick={handleApartarClick}
                                disabled={!isActive}
                                className={`bg-green-600 text-white font-bold py-3 px-6 rounded-full text-base hover:bg-green-700 transition-all transform hover:scale-105 disabled:bg-gray-400 disabled:scale-100 disabled:cursor-not-allowed flex items-center gap-2 ${isActive ? 'animate-attention' : ''}`}
                            >
                                <Ticket size={20}/> Apartar
                            </button>
                        );
                    })()}
                </div>
            </footer>
        </div>
    );
};

export default Boletos;
